import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";
import api from "../../api/client";
import StatusBadge from "../shared/StatusBadge";
import "./ProjectsList.css";

const STATUS_OPTIONS = ["active", "on_hold", "completed", "cancelled"];

const BLANK_FORM = {
    client_id: "",
    title: "",
    description: "",
    scope: "",
    status: "active",
    start_date: "",
    end_date: "",
    budget_cad: "",
};

const formatCAD = (n) =>
    n != null
        ? new Intl.NumberFormat("en-CA", {
              style: "currency",
              currency: "CAD",
          }).format(n)
        : "—";

const stripHtml = (html) => {
    if (!html) return "";
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || "";
};

const ProjectsList = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [projects, setProjects] = useState([]);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ ...BLANK_FORM });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [filterClient, setFilterClient] = useState("");
    const [filterStatus, setFilterStatus] = useState("");
    const [statusBusyId, setStatusBusyId] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        const calls = isAdmin
            ? [api.projects(), api.allClients()]
            : [api.projects()];
        Promise.all(calls)
            .then(([pRes, cRes]) => {
                setProjects(pRes.projects || []);
                if (cRes) {
                    setClients(
                        (cRes.data || []).filter((c) => c.role === "client"),
                    );
                }
            })
            .finally(() => setLoading(false));
    }, [isAdmin]);

    useEffect(() => {
        load();
    }, [load]);

    const handleChange = (e) =>
        setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError("");
        try {
            await api.createProject({
                ...form,
                budget_cad: form.budget_cad ? parseFloat(form.budget_cad) : null,
            });
            setForm({ ...BLANK_FORM });
            setShowForm(false);
            load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("Delete this project?")) return;
        try {
            await api.deleteProject(id);
            setProjects((prev) => prev.filter((p) => p.id !== id));
        } catch (err) {
            alert(err.message);
        }
    };

    const handleStatusChange = async (projectId, status) => {
        if (
            status === "cancelled" &&
            !window.confirm(
                "Mark this project as cancelled? This won't delete any data.",
            )
        ) {
            return;
        }
        setStatusBusyId(projectId);
        try {
            const { project: updated } = await api.updateProject(projectId, {
                status,
            });
            setProjects((prev) =>
                prev.map((p) => (p.id === projectId ? updated : p)),
            );
        } catch (err) {
            alert(err.message);
        } finally {
            setStatusBusyId(null);
        }
    };

    const filtered = projects.filter(
        (p) =>
            (!filterClient || String(p.client_id) === String(filterClient)) &&
            (!filterStatus || p.status === filterStatus),
    );

    return (
        <div className="projects-list">
            <div className="section-header">
                <h2>Projects</h2>
                {isAdmin && (
                    <button
                        className="btn btn--primary btn--sm"
                        onClick={() => {
                            setShowForm((v) => !v);
                            setError("");
                        }}
                    >
                        {showForm ? "Cancel" : "+ New Project"}
                    </button>
                )}
            </div>

            {isAdmin && showForm && (
                <div className="projects-list__form card">
                    <h3>Create Project</h3>
                    <form onSubmit={handleSubmit}>
                        <div className="projects-list__form-grid">
                            <div className="form-group">
                                <label>Client *</label>
                                <select
                                    name="client_id"
                                    value={form.client_id}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="">— Select client —</option>
                                    {clients.map((c) => (
                                        <option key={c.id} value={c.id}>
                                            {c.name}{" "}
                                            {c.company ? `(${c.company})` : ""}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Title *</label>
                                <input
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    required
                                    placeholder="Website Redesign"
                                />
                            </div>
                            <div className="form-group">
                                <label>Status</label>
                                <select
                                    name="status"
                                    value={form.status}
                                    onChange={handleChange}
                                >
                                    {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>
                                            {s.replace("_", " ")}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Budget (CAD)</label>
                                <input
                                    type="number"
                                    name="budget_cad"
                                    value={form.budget_cad}
                                    onChange={handleChange}
                                    min="0"
                                    step="0.01"
                                    placeholder="5000.00"
                                />
                            </div>
                            <div className="form-group">
                                <label>Start Date</label>
                                <input
                                    type="date"
                                    name="start_date"
                                    value={form.start_date}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-group">
                                <label>End Date</label>
                                <input
                                    type="date"
                                    name="end_date"
                                    value={form.end_date}
                                    onChange={handleChange}
                                />
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Description</label>
                            <textarea
                                name="description"
                                value={form.description}
                                onChange={handleChange}
                                placeholder="Brief overview of the project…"
                                rows={3}
                            />
                        </div>
                        <div className="form-group">
                            <label>Scope</label>
                            <textarea
                                name="scope"
                                value={form.scope}
                                onChange={handleChange}
                                placeholder="Detailed scope, deliverables, milestones…"
                                rows={4}
                            />
                        </div>
                        {error && (
                            <p className="error-message">{error}</p>
                        )}
                        <button
                            type="submit"
                            className="btn btn--primary btn--sm"
                            disabled={saving}
                        >
                            {saving ? "Saving…" : "Create Project"}
                        </button>
                    </form>
                </div>
            )}

            {/* Filters — admin only */}
            {isAdmin && (
                <div className="projects-list__filter">
                    <select
                        value={filterClient}
                        onChange={(e) => setFilterClient(e.target.value)}
                    >
                        <option value="">All clients</option>
                        {clients.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.name}
                            </option>
                        ))}
                    </select>
                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                    >
                        <option value="">All statuses</option>
                        {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>
                                {s.replace("_", " ")}
                            </option>
                        ))}
                    </select>
                </div>
            )}

            {loading ? (
                <p style={{ color: "var(--color-text-muted)" }}>
                    {t("common.loading")}
                </p>
            ) : filtered.length === 0 ? (
                <p style={{ color: "var(--color-text-muted)" }}>
                    No projects yet.
                </p>
            ) : (
                <div className="projects-list__grid">
                    {filtered.map((project) => (
                        <div
                            key={project.id}
                            className="projects-list__card card"
                        >
                            <div className="projects-list__card-header">
                                <h3
                                    className="projects-list__card-title"
                                    onClick={() =>
                                        navigate(
                                            `/admin/projects/${project.id}`,
                                        )
                                    }
                                >
                                    {project.title}
                                </h3>
                                {isAdmin ? (
                                    <label
                                        className={`projects-list__status-select status-badge status-badge--${project.status}`}
                                        title="Change project status"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        <select
                                            value={project.status}
                                            disabled={
                                                statusBusyId === project.id
                                            }
                                            onChange={(e) =>
                                                handleStatusChange(
                                                    project.id,
                                                    e.target.value,
                                                )
                                            }
                                        >
                                            {STATUS_OPTIONS.map((s) => (
                                                <option key={s} value={s}>
                                                    {s.replace("_", " ")}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                ) : (
                                    <StatusBadge status={project.status} />
                                )}
                            </div>
                            {project.client && (
                                <p className="projects-list__client">
                                    {project.client.name}
                                    {project.client.company
                                        ? ` · ${project.client.company}`
                                        : ""}
                                </p>
                            )}
                            {project.description && (
                                <p className="projects-list__desc">
                                    {stripHtml(project.description)}
                                </p>
                            )}
                            {(project.stages || []).length > 0 && (
                                <div className="projects-list__progress">
                                    <span className="projects-list__progress-bar">
                                        <span
                                            className="projects-list__progress-fill"
                                            style={{
                                                width: `${
                                                    (project.stages.filter(
                                                        (s) =>
                                                            s.status ===
                                                            "completed",
                                                    ).length /
                                                        project.stages
                                                            .length) *
                                                    100
                                                }%`,
                                            }}
                                        />
                                    </span>
                                    <span className="projects-list__progress-label">
                                        {
                                            project.stages.filter(
                                                (s) =>
                                                    s.status === "completed",
                                            ).length
                                        }
                                        /{project.stages.length}
                                    </span>
                                </div>
                            )}
                            <div className="projects-list__meta">
                                {project.budget_cad != null && (
                                    <span>
                                        Budget: {formatCAD(project.budget_cad)}
                                    </span>
                                )}
                                {project.start_date && (
                                    <span>
                                        {project.start_date}
                                        {project.end_date
                                            ? ` → ${project.end_date}`
                                            : ""}
                                    </span>
                                )}
                            </div>
                            <div className="projects-list__actions">
                                <button
                                    className="btn btn--ghost btn--sm"
                                    onClick={() =>
                                        navigate(
                                            isAdmin
                                                ? `/admin/projects/${project.id}`
                                                : `/dashboard/projects/${project.id}`,
                                        )
                                    }
                                >
                                    {isAdmin ? "View / Edit" : "View"}
                                </button>
                                {isAdmin && (
                                    <button
                                        className="btn btn--danger btn--sm"
                                        onClick={() =>
                                            handleDelete(project.id)
                                        }
                                    >
                                        Delete
                                    </button>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ProjectsList;
